import { Accordion, Drawer, Heading } from '@boltenergy-be/design-system'
import SearchBar from 'components/search/SearchBar.tsx'
import parse from 'html-react-parser'
import { Fragment, useMemo, useState } from 'react'
import { searchMultipleKeys } from 'utils/objects.ts'
import styles from './FaqDrawer.module.scss'
import { routes } from 'types/routes.ts'
import Link from 'components/link/Link.tsx'
import { FaqDrawerProps } from './types.ts'
import useWindowSize from 'hooks/useWindowSize.tsx'
import { BlocksRenderer } from '@strapi/blocks-react-renderer'
import { FaqQuestion } from 'types/cms.ts'
import { useTranslation } from 'react-i18next'

const EMPTY_FAQ_SUBCATEGORY = 'General'

const FaqDrawer = ({ isOpen, onRequestClose, questions, title, hasSearch = true, tagsToHide }: FaqDrawerProps) => {
  // i18n
  const { t } = useTranslation(['common', 'support'])

  // Window size
  const { isTablet } = useWindowSize()

  // Local state
  const [searchQuery, setSearchQuery] = useState<string>('')

  // Memoized filtered questions
  const filteredQuestions = useMemo<FaqQuestion[]>(() => {
    const questionsFilteredByTags = tagsToHide?.length
      ? questions.filter((q) => !q.faqTags?.some((tag) => tagsToHide.includes(tag.name)))
      : questions

    const searchResults = hasSearch
      ? questionsFilteredByTags.filter((q) => searchMultipleKeys([q.question, q.answer || ''], searchQuery))
      : questionsFilteredByTags

    // Sort the results based on the subcategory (alphabetically), placing those without a subcategory at the start
    return searchResults.sort((a, b) => {
      const aSubcat = a.subcategory?.name
      const bSubcat = b.subcategory?.name
      if (!aSubcat && !bSubcat) return 0
      if (!aSubcat) return -1
      if (!bSubcat) return 1
      return aSubcat.localeCompare(bSubcat)
    })
  }, [hasSearch, questions, searchQuery, tagsToHide])

  // Group questions by subcategory
  const groupedQuestions = useMemo(() => {
    return filteredQuestions.reduce<Record<string, FaqQuestion[]>>((acc, question) => {
      const subcategory = question?.subcategory?.name || EMPTY_FAQ_SUBCATEGORY
      if (!acc[subcategory]) {
        acc[subcategory] = []
      }
      acc[subcategory].push(question)
      return acc
    }, {})
  }, [filteredQuestions])

  return (
    <Drawer {...{ isOpen, onRequestClose }} title={title} className={styles.faq}>
      {hasSearch && (
        <SearchBar className={styles.search} onInput={(query) => setSearchQuery(query)} placeholder={t(($) => $.faq.searchPlaceholder)} />
      )}

      {Object.keys(groupedQuestions).length > 0 ? (
        Object.entries(groupedQuestions).map(([subcategory, questions]) => (
          <Fragment key={subcategory}>
            {subcategory !== EMPTY_FAQ_SUBCATEGORY && (
              <Heading as="h2" variant="h5" className="mt-400">
                {subcategory}
              </Heading>
            )}

            <Accordion
              variant={isTablet ? 'transparent' : 'filled'}
              items={questions.map((item) => ({
                title: parse(item?.question),
                children: item.content ? (
                  <BlocksRenderer
                    content={item.content}
                    blocks={{
                      link: ({ children, url }) => (
                        <Link
                          as="a"
                          variant="inline"
                          href={url}
                          target={url.startsWith('http') && !url.includes('my.boltenergie') ? '_blank' : undefined}
                        >
                          {children}
                        </Link>
                      )
                    }}
                  />
                ) : (
                  parse(item?.answer || '')
                )
              }))}
            />
          </Fragment>
        ))
      ) : (
        <div className={styles['no-results-container']}>
          <small>
            {t(($) => $.footer.noAnswer, {
              ns: 'support'
            })}
          </small>
          <Link href={routes.SUPPORT_CONTACT} variant="secondary" size="small">
            {t(($) => $.contact.tab, {
              ns: 'support'
            })}
          </Link>
        </div>
      )}
    </Drawer>
  )
}

export default FaqDrawer
